import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import * as cloudApi from '../services/api';
import { Expense, MONTHS } from '../types';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Loader2,
  DollarSign,
  FileText,
  Fuel,
  Car,
  Utensils,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react';

// ─── Day status helpers ─────────────────────────────────────────────────────

type DayStatus = 'empty' | 'flagged' | 'settlement' | 'expenses' | 'travel';

function getDayStatus(dayExpenses: Expense[]): DayStatus {
  if (dayExpenses.length === 0) return 'empty';
  const hasFlagged = dayExpenses.some(
    (e) => e.amount === 0 || e.amount === undefined || e.amount === null
  );
  if (hasFlagged) return 'flagged';
  const hasSettlement = dayExpenses.some((e) => e.category === 'settlements');
  if (hasSettlement) return 'settlement';
  const onlyTravelFuel = dayExpenses.every(
    (e) => e.category === 'travel' || e.category === 'fuel'
  );
  if (onlyTravelFuel) return 'travel';
  return 'expenses';
}

const STATUS_STYLES: Record<DayStatus, { bg: string; border: string; label: string }> = {
  settlement: {
    bg: 'bg-emerald-700/40',
    border: 'border-emerald-500/60',
    label: 'Settlement',
  },
  expenses: {
    bg: 'bg-amber-600/30',
    border: 'border-amber-400/50',
    label: 'Expenses',
  },
  travel: {
    bg: 'bg-blue-600/30',
    border: 'border-blue-400/50',
    label: 'Travel/Fuel',
  },
  empty: {
    bg: 'bg-white/5',
    border: 'border-white/10',
    label: 'Empty',
  },
  flagged: {
    bg: 'bg-red-700/30',
    border: 'border-red-500/60',
    label: 'Flagged',
  },
};

// ─── Category dot colours ────────────────────────────────────────────────────

const CATEGORY_DOT: Record<string, string> = {
  settlements: 'bg-yellow-400',
  travel: 'bg-blue-400',
  fuel: 'bg-orange-400',
  meals: 'bg-green-400',
  truck: 'bg-purple-400',
  tolls: 'bg-cyan-400',
  office: 'bg-pink-400',
  miscellaneous: 'bg-slate-400',
  bol: 'bg-teal-400',
  statements: 'bg-indigo-400',
};

function categoryDot(cat: string) {
  return CATEGORY_DOT[cat] ?? 'bg-white/40';
}

// ─── Category icon for the side panel ───────────────────────────────────────

function categoryIcon(cat: string) {
  switch (cat) {
    case 'settlements': return <DollarSign className="w-4 h-4 text-yellow-400" />;
    case 'travel':      return <Car className="w-4 h-4 text-blue-400" />;
    case 'fuel':        return <Fuel className="w-4 h-4 text-orange-400" />;
    case 'meals':       return <Utensils className="w-4 h-4 text-green-400" />;
    default:            return <FileText className="w-4 h-4 text-white/50" />;
  }
}

function fmt(amount: number | undefined) {
  if (amount === undefined || amount === null) return '—';
  return `$${amount.toFixed(2)}`;
}

function ordinal(d: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = d % 100;
  return d + (s[(v - 20) % 10] || s[v] || s[0]);
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function Coverage() {
  const today = new Date();
  const { isAuthenticated } = useAuth();
  const { mode } = useMode();

  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Load expenses whenever month/year/mode changes
  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    setSelectedDay(null);
    cloudApi
      .getExpensesByMonthYear(viewYear, viewMonth, mode)
      .then(setExpenses)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [viewYear, viewMonth, mode, isAuthenticated]);

  // Group by day number (1-31)
  const byDay = useMemo(() => {
    const map: Record<number, Expense[]> = {};
    expenses.forEach((e) => {
      const d = e.day ?? parseInt(e.date?.split('-')[2] ?? '0', 10);
      if (!d) return;
      if (!map[d]) map[d] = [];
      map[d].push(e);
    });
    return map;
  }, [expenses]);

  // Calendar geometry
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth - 1, 1).getDay(); // 0=Sun

  // Summary stats
  const activeDays = Object.keys(byDay).length;
  const totalSettlements = useMemo(
    () =>
      expenses
        .filter((e) => e.category === 'settlements')
        .reduce((s, e) => s + (e.amount ?? 0), 0),
    [expenses]
  );
  const totalExpenses = useMemo(
    () =>
      expenses
        .filter((e) => e.category !== 'settlements')
        .reduce((s, e) => s + (e.amount ?? 0), 0),
    [expenses]
  );

  // Navigation
  const prevMonth = () => {
    if (viewMonth === 1) { setViewMonth(12); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 12) { setViewMonth(1); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  // Build calendar cell list: [null, null, ..., 1, 2, ..., daysInMonth]
  const cells: (number | null)[] = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  // Selected day data
  const selectedExpenses = selectedDay ? (byDay[selectedDay] ?? []) : [];
  const monthName = MONTHS[viewMonth - 1];

  // Group selected day expenses by category label
  const byCategory = useMemo(() => {
    const map: Record<string, Expense[]> = {};
    selectedExpenses.forEach((e) => {
      const k = e.category as string;
      if (!map[k]) map[k] = [];
      map[k].push(e);
    });
    return map;
  }, [selectedExpenses]);

  const missingCategories = ['fuel', 'meals', 'travel'].filter(
    (c) => !byCategory[c]
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-shuttle-sand to-amber-500 flex items-center justify-center shadow-lg">
          <CalendarDays className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Coverage Calendar</h1>
          <p className="text-white/50 text-sm">What's imported vs missing, day by day</p>
        </div>
      </div>

      {/* Month / year picker */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4 flex items-center justify-between gap-4">
        <button
          onClick={prevMonth}
          className="p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3">
          <select
            value={viewMonth}
            onChange={(e) => setViewMonth(Number(e.target.value))}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-shuttle-sand/50"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1} className="bg-slate-800 text-white">
                {m}
              </option>
            ))}
          </select>

          <select
            value={viewYear}
            onChange={(e) => setViewYear(Number(e.target.value))}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-shuttle-sand/50"
          >
            {Array.from({ length: 10 }, (_, i) => today.getFullYear() - 8 + i).map((y) => (
              <option key={y} value={y} className="bg-slate-800 text-white">
                {y}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={nextMonth}
          className="p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-white/70">
        {(Object.entries(STATUS_STYLES) as [DayStatus, typeof STATUS_STYLES[DayStatus]][]).map(
          ([status, s]) => (
            <span key={status} className="flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm border ${s.bg} ${s.border}`} />
              {s.label}
            </span>
          )
        )}
      </div>

      {/* Loading / error */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-white/60 gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading {monthName} {viewYear}…</span>
        </div>
      )}
      {error && (
        <div className="bg-red-500/20 border border-red-500/40 rounded-xl p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* ── Calendar grid ── */}
          <div className="flex-1 min-w-0">
            {/* Day-of-week header */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="text-center text-xs font-semibold text-white/40 py-1">
                  {d}
                </div>
              ))}
            </div>

            {/* Cells */}
            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, idx) => {
                if (!day) {
                  return <div key={`empty-${idx}`} />;
                }
                const dayExpenses = byDay[day] ?? [];
                const status = getDayStatus(dayExpenses);
                const styles = STATUS_STYLES[status];
                const isSelected = selectedDay === day;
                const isToday =
                  day === today.getDate() &&
                  viewMonth === today.getMonth() + 1 &&
                  viewYear === today.getFullYear();

                // unique categories present
                const cats = [...new Set(dayExpenses.map((e) => e.category as string))];
                const settlementTotal = dayExpenses
                  .filter((e) => e.category === 'settlements')
                  .reduce((s, e) => s + (e.amount ?? 0), 0);

                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDay(isSelected ? null : day)}
                    className={`
                      relative flex flex-col items-start p-1.5 rounded-xl border transition-all text-left
                      min-h-[80px]
                      ${styles.bg} ${styles.border}
                      ${isSelected ? 'ring-2 ring-shuttle-sand shadow-lg shadow-shuttle-sand/20' : 'hover:brightness-110'}
                      ${isToday ? 'ring-1 ring-white/50' : ''}
                    `}
                  >
                    {/* Day number */}
                    <span className={`text-xs font-bold ${isToday ? 'text-shuttle-sand' : 'text-white/80'}`}>
                      {day}
                    </span>

                    {/* Category dots */}
                    {cats.length > 0 && (
                      <div className="flex flex-wrap gap-0.5 mt-1">
                        {cats.slice(0, 5).map((c) => (
                          <span
                            key={c}
                            className={`w-1.5 h-1.5 rounded-full ${categoryDot(c)}`}
                            title={c}
                          />
                        ))}
                      </div>
                    )}

                    {/* Settlement total */}
                    {settlementTotal > 0 && (
                      <span className="mt-auto text-[10px] font-semibold text-emerald-300 leading-none">
                        ${settlementTotal.toFixed(0)}
                      </span>
                    )}

                    {/* Flagged icon */}
                    {status === 'flagged' && (
                      <AlertTriangle className="absolute top-1 right-1 w-3 h-3 text-red-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Side panel ── */}
          <div className="lg:w-72 xl:w-80">
            {selectedDay ? (
              <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-5 space-y-4 animate-fade-in">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-white flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-shuttle-sky" />
                    {monthName} {ordinal(selectedDay)}, {viewYear}
                  </h2>
                  <button
                    onClick={() => setSelectedDay(null)}
                    className="text-white/40 hover:text-white/70 transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>

                {selectedExpenses.length === 0 ? (
                  <p className="text-white/40 text-sm italic">No entries for this day.</p>
                ) : (
                  Object.entries(byCategory).map(([cat, items]) => (
                    <div key={cat}>
                      <div className="flex items-center gap-2 mb-1.5">
                        {categoryIcon(cat)}
                        <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                          {cat} ({items.length})
                        </span>
                      </div>
                      <div className="space-y-1 pl-6">
                        {items.map((e) => (
                          <div key={e.id} className="flex items-center justify-between gap-2">
                            <span className="text-xs text-white/60 truncate max-w-[140px]" title={e.description || e.filename}>
                              {e.description || e.filename || '—'}
                            </span>
                            <span className={`text-xs font-mono ${(e.amount ?? 0) === 0 ? 'text-red-400' : 'text-white/80'}`}>
                              {fmt(e.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}

                {/* Missing categories */}
                {missingCategories.length > 0 && (
                  <div className="border-t border-white/10 pt-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      <span className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
                        Missing
                      </span>
                    </div>
                    <div className="pl-6 space-y-1">
                      {missingCategories.map((c) => (
                        <p key={c} className="text-xs text-white/40 italic">
                          No {c} receipts found
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6 flex flex-col items-center justify-center text-center gap-3 min-h-[200px]">
                <CalendarDays className="w-8 h-8 text-white/20" />
                <p className="text-white/30 text-sm">Click a day to see its entries</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Summary bar ── */}
      {!loading && !error && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4">
          <div className="flex flex-wrap gap-x-8 gap-y-2 items-center">
            <span className="text-white font-semibold text-sm">
              {monthName} {viewYear}
            </span>
            <SumStat icon={<CheckCircle className="w-4 h-4 text-emerald-400" />} label="Active days" value={String(activeDays)} />
            <SumStat icon={<DollarSign className="w-4 h-4 text-yellow-400" />} label="Settlements" value={`$${totalSettlements.toFixed(2)}`} />
            <SumStat icon={<FileText className="w-4 h-4 text-shuttle-sky" />} label="Expenses" value={`$${totalExpenses.toFixed(2)}`} />
            <SumStat icon={<CalendarDays className="w-4 h-4 text-white/40" />} label="Days with entries" value={String(activeDays)} />
            <SumStat icon={<CalendarDays className="w-4 h-4 text-white/20" />} label="Empty days" value={String(daysInMonth - activeDays)} />
          </div>
        </div>
      )}

      {/* Mobile list fallback (shown only on small screens instead of grid) */}
      {!loading && !error && (
        <div className="md:hidden space-y-2">
          <h3 className="text-white/50 text-xs font-semibold uppercase tracking-wider">
            List View
          </h3>
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
            const dayExpenses = byDay[day] ?? [];
            if (dayExpenses.length === 0) return null;
            const status = getDayStatus(dayExpenses);
            const styles = STATUS_STYLES[status];
            return (
              <div
                key={day}
                className={`rounded-xl border px-4 py-3 flex items-center justify-between ${styles.bg} ${styles.border}`}
              >
                <div>
                  <span className="text-white font-semibold text-sm">
                    {monthName} {day}
                  </span>
                  <div className="flex gap-1.5 mt-1">
                    {[...new Set(dayExpenses.map((e) => e.category as string))].map((c) => (
                      <span key={c} className={`w-2 h-2 rounded-full ${categoryDot(c)}`} />
                    ))}
                  </div>
                </div>
                <span className="text-white/60 text-xs">{dayExpenses.length} entries</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SumStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-white/50 text-xs">{label}:</span>
      <span className="text-white text-xs font-semibold">{value}</span>
    </div>
  );
}
